import React from 'react';
import styled from 'styled-components';
import { useStoreState, useStoreActions } from '../store';
import { useLocale } from '../hooks/useLocale';
import { useLongPress } from '../hooks/useLongPress';
import { breakpoints } from '../utils/breakpoints';
import logo from '../assets/images/logo.png';

interface ContextMenuOverlayProps {
  children: React.ReactNode;
}

export function ContextMenuOverlay(props: ContextMenuOverlayProps) {
  const locale = useLocale();
  const contextMenuVisible = useStoreState(state => state.player.contextMenuVisible);
  const contextMenuPosition = useStoreState(state => state.player.contextMenuPosition);
  const version = useStoreState(state => state.config.version);
  const actions = useStoreActions(actions => actions);
  const containerRef = React.useRef<any>(null);

  function handleBackdropClick() {
    actions.player.setContextMenuVisible(false);
    actions.player.setContextMenuPosition(null);
  }

  /**
   * When user right-clicks the player,
   * show the player context menu.
   */
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    actions.player.setContextMenuVisible(true);
    actions.player.setContextMenuPosition({
      x: event.clientX - rect.x,
      y: event.clientY - rect.y,
    });
  };

  /**
   * When user long-presses the player,
   * show the player context menu (for touchscreens).
   */
  const handleLongPress = () => {
    actions.player.setContextMenuVisible(true);
  };

  const longPressEvent = useLongPress(handleLongPress, {
    delay: 600,
    isPreventDefault: false,
  });

  return (
    <Container
      ref={containerRef}
      onContextMenu={handleContextMenu}
      onTouchStart={longPressEvent.onTouchStart}
      onTouchEnd={longPressEvent.onTouchEnd}
    >
      {props.children}
      {contextMenuVisible && (
        <Overlay>
          <ContextMenuBackdrop onClick={handleBackdropClick} />
          <ContextMenu position={contextMenuPosition}>
            <MenuItem>{locale.translations.ContextMenuOverlay.Version(version)}</MenuItem>
            <MenuItem>
              <Signature>
                <Logo /> <PoweredBy>{locale.translations.ContextMenuOverlay.PoweredByBeachbody}</PoweredBy>
              </Signature>
            </MenuItem>
          </ContextMenu>
        </Overlay>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
`;

const ContextMenuBackdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const ContextMenu = styled.div<{ position: { x: number; y: number } | null }>`
  position: ${p => (p.position ? 'absolute' : 'relative')};
  top: ${p => (p.position ? `${p.position.y}px` : 'initial')};
  left: ${p => (p.position ? `${p.position.x}px` : 'initial')};
  background: rgba(0, 0, 0, 0.5);
  border: solid 1px rgba(0, 0, 0, 0.5);
  border-radius: 0.25em;
  font-size: 13px;
  font-family: ProximaNova-Regular;
  color: white;
  z-index: 1;

  @media (max-width: ${breakpoints.xlarge}px) {
    position: relative;
    top: initial;
    left: initial;
  }
`;

const MenuItem = styled.div`
  padding: 1em 2em;
  width: 200px;
  border-bottom: solid 1px rgba(255, 255, 255, 0.25);
  cursor: pointer;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Signature = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.div`
  display: inline-block;
  width: 30px;
  min-width: 30px;
  height: 30px;
  background: url(${logo}) center / contain no-repeat;
  margin-right: 1em;
`;

const PoweredBy = styled.div`
  display: inline-block;
`;
